/**
 * The first-goal flow without an account (T-156, the web's version of the app's onboarding,
 * Faz 19): what the visitor has done so far, kept in this browser so a closed tab picks up where
 * it left off. Never the password: it lives only in the sign-up form and is sent once.
 */
import { AIGoalPlan, PlanPreferences, PlanQuestion } from '../services/aiService';

export const ONBOARDING_STEPS = [
  'intro',
  'goal',
  'questions',
  'generating',
  'plan',
  'signup',
  'verify',
  'welcome',
] as const;

export type OnboardingStep = typeof ONBOARDING_STEPS[number];

/** The steps the progress bar counts (the intro and the welcome stand apart). */
export const PROGRESS_STEPS: readonly OnboardingStep[] = ['goal', 'questions', 'generating', 'plan', 'signup', 'verify'];

export interface OnboardingState {
  /** The step the visitor is on; the page reopens here. */
  step: OnboardingStep;
  goal: string;
  /** The AI's questions about this goal, once asked (they are asked once per goal). */
  questions: PlanQuestion[] | null;
  /** What the questions step gave the plan (deadline, time, level, answers); null until it was
   * answered or skipped. */
  preferences: PlanPreferences | null;
  /** The demo job being made (T-155), to keep polling after a reload. */
  jobId: string | null;
  plan: AIGoalPlan | null;
  /** Changes left on the demo plan before signing up. */
  revisionsLeft: number;
  /** The plan is one of the ready samples (the demo was busy). */
  sample: boolean;
  /** The address the code was sent to, while it waits to be verified. */
  pendingEmail: string | null;
  firstName: string;
  /** The plan reached the account (T-158): it must not be saved twice. */
  transferredGoalId: string | null;
  updatedAt: number;
}

export const STATE_KEY = 'riselio:onboarding';
export const DEVICE_KEY = 'riselio:demoDevice';
export const HAD_SESSION_KEY = 'riselio:hadSession';

/** A flow left this long starts over; the server keeps demo plans 7 days (T-148). */
export const STATE_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

export const initialState = (): OnboardingState => ({
  step: 'intro',
  goal: '',
  questions: null,
  preferences: null,
  jobId: null,
  plan: null,
  revisionsLeft: 1,
  sample: false,
  pendingEmail: null,
  firstName: '',
  transferredGoalId: null,
  updatedAt: Date.now(),
});

const isStep = (value: unknown): value is OnboardingStep =>
  typeof value === 'string' && (ONBOARDING_STEPS as readonly string[]).includes(value);

/** localStorage may be missing or refuse (private mode, blocked storage): never throw. */
const read = (key: string): string | null => {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
};

const write = (key: string, value: string | null) => {
  try {
    if (value === null) window.localStorage.removeItem(key);
    else window.localStorage.setItem(key, value);
  } catch {
    // The flow still works for this tab; it just won't resume.
  }
};

export const loadState = (now: number = Date.now()): OnboardingState => {
  const raw = read(STATE_KEY);
  if (!raw) return initialState();
  try {
    const saved = JSON.parse(raw) as Partial<OnboardingState>;
    if (!isStep(saved.step) || typeof saved.updatedAt !== 'number' || now - saved.updatedAt > STATE_MAX_AGE_MS) {
      return initialState();
    }
    return { ...initialState(), ...saved, step: saved.step };
  } catch {
    return initialState();
  }
};

export const saveState = (state: OnboardingState) => write(STATE_KEY, JSON.stringify(state));

export const clearState = () => write(STATE_KEY, null);

/** True when this browser holds a flow beyond its first screen. */
export const hasStartedFlow = (now: number = Date.now()) => {
  const state = loadState(now);
  return state.step !== 'intro' || state.goal !== '';
};

const randomId = (): string => {
  const cryptoApi: Crypto | undefined = typeof window !== 'undefined' ? window.crypto : undefined;
  if (cryptoApi?.randomUUID) return cryptoApi.randomUUID();
  const bytes = new Uint8Array(16);
  if (cryptoApi?.getRandomValues) cryptoApi.getRandomValues(bytes);
  else for (let i = 0; i < bytes.length; i += 1) bytes[i] = Math.floor(Math.random() * 256);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
};

/**
 * This browser's demo device id (X-Demo-Device): random, made once, never personal. Kept apart
 * from the flow so starting over does not reset the daily demo count.
 */
export const demoDeviceId = (): string => {
  const saved = read(DEVICE_KEY);
  if (saved && /^[A-Za-z0-9_-]{16,128}$/.test(saved)) return saved;
  const id = randomId();
  write(DEVICE_KEY, id);
  return id;
};

/** Settings only the app writes: whoever has them has used Riselio in this browser. */
const SIGNS_OF_AN_ACCOUNT = ['theme', 'inspirationScreenEnabled', 'riselio:pomodoro', 'aiPlanPreferences',
  'riselio:shownReminders', 'auth-view'];

/** Remember that someone signed in here: a signed-out visitor then gets the login, not the flow. */
export const markHadSession = () => write(HAD_SESSION_KEY, '1');

export const hadSession = (): boolean =>
  read(HAD_SESSION_KEY) === '1' || SIGNS_OF_AN_ACCOUNT.some((key) => read(key) !== null);

/**
 * The step a visitor may open: a later one needs what the earlier ones made (a plan to save, an
 * address to verify). Asking for more lands on the furthest one reached.
 */
export const allowedStep = (state: OnboardingState, wanted: OnboardingStep): OnboardingStep => {
  const ok = (step: OnboardingStep): boolean => {
    switch (step) {
      case 'intro':
      case 'goal':
        return true;
      case 'questions':
        return state.goal.trim() !== '';
      case 'generating':
        return state.goal.trim() !== '' && (state.preferences !== null || state.jobId !== null);
      case 'plan':
      case 'signup':
        return state.plan !== null;
      case 'verify':
        return state.plan !== null && !!state.pendingEmail;
      case 'welcome':
        return state.plan !== null && (state.step === 'welcome' || !!state.transferredGoalId);
      default:
        return false;
    }
  };
  if (ok(wanted)) return wanted;
  const reached = ONBOARDING_STEPS.indexOf(state.step);
  for (let i = Math.min(reached, ONBOARDING_STEPS.length - 1); i >= 0; i -= 1) {
    if (ok(ONBOARDING_STEPS[i])) return ONBOARDING_STEPS[i];
  }
  return 'intro';
};

/** '#/start' or '#/start/plan' -> the step asked for; anything else -> null. */
export const stepFromHash = (hash: string): OnboardingStep | null | 'resume' => {
  const path = hash.replace(/^#/, '').split('?')[0];
  if (path === '/start' || path === '/start/') return 'resume';
  const match = /^\/start\/([a-z]+)$/.exec(path);
  if (!match) return null;
  return isStep(match[1]) ? match[1] : 'resume';
};

export const isStartHash = (hash: string) => stepFromHash(hash) !== null;

export const startHash = (step?: OnboardingStep) => (step ? `#/start/${step}` : '#/start');

/**
 * A signed-in user stays in (or comes back to) the flow only to save its plan (T-158): once the
 * flow reached the welcome, or when the plan waits for this very address (the emailed link was
 * used instead of the code; the flow then moves on to the welcome). Another account signed in
 * in this browser never gets someone else's plan. A plan already saved keeps its welcome only
 * on the flow's own address; anywhere else the flow is done and forgotten.
 */
export const flowAwaitsAccount = (email?: string | null, hash = '', now: number = Date.now()): boolean => {
  const flow = loadState(now);
  if (!flow.plan) return false;
  if (flow.transferredGoalId) {
    if (flow.step === 'welcome' && isStartHash(hash)) return true;
    clearState();
    return false;
  }
  if (flow.step === 'welcome') return true;
  if (email && flow.pendingEmail && flow.pendingEmail.toLowerCase() === email.toLowerCase()) {
    saveState({ ...flow, step: 'welcome', pendingEmail: null, updatedAt: now });
    return true;
  }
  return false;
};
